import { Joyride, EVENTS, STATUS } from 'react-joyride';
import type { EventData, Controls } from 'react-joyride';
import { CLIENT_TUTORIAL_STEPS } from '../../lib/tutorial-steps';
import { useTutorialStore } from '../../stores/tutorial-store';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import { useEffect } from 'react';

export function TutorialProvider() {
  const { isRunning, start, stop, markCompleted, hasCompleted } = useTutorialStore();
  const user = useAuthStore((s) => s.user);
  const dark = useThemeStore((s) => s.dark);

  // Auto-start tutorial for first-time clients
  useEffect(() => {
    if (user && user.role === 'CLIENT' && !hasCompleted(user.id)) {
      // Small delay to let the layout render so targets exist
      const timer = setTimeout(() => start(), 500);
      return () => clearTimeout(timer);
    }
  }, [user, hasCompleted, start]);

  const handleEvent = (data: EventData, controls: Controls) => {
    const { type, status } = data;

    if (type === EVENTS.TOUR_END) {
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        stop();
        if (user) markCompleted(user.id);
      }
    }
  };

  return (
    <Joyride
      steps={CLIENT_TUTORIAL_STEPS}
      run={isRunning}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      locale={{
        back: 'Înapoi',
        close: 'Închide',
        last: 'Terminare',
        next: 'Următorul',
        open: 'Deschide',
        skip: 'Sari peste',
      }}
      options={{
        primaryColor: dark ? '#60a5fa' : '#2563eb',
        backgroundColor: dark ? '#1e293b' : '#ffffff',
        textColor: dark ? '#e2e8f0' : '#1e293b',
        arrowColor: dark ? '#1e293b' : '#ffffff',
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10000,
        skipBeacon: true,
        showProgress: true,
        overlayClickAction: false,
        closeButtonAction: 'skip',
        blockTargetInteraction: true,
      }}
      styles={{
        tooltip: {
          borderRadius: 12,
          padding: 20,
        },
        tooltipContent: {
          fontSize: 14,
          lineHeight: 1.6,
        },
        buttonPrimary: {
          borderRadius: 8,
          fontSize: 14,
          padding: '8px 16px',
        },
        buttonBack: {
          borderRadius: 8,
          fontSize: 14,
          color: dark ? '#94a3b8' : '#64748b',
        },
        buttonSkip: {
          fontSize: 13,
          color: dark ? '#94a3b8' : '#64748b',
        },
      }}
    />
  );
}
